import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DynamicBackground from '../components/DynamicBackground';
import ChatWindow from '../components/ChatWindow';
import ParliamentIllustration from '../components/ParliamentIllustration';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { googleSignIn, isAuthenticated, getCurrentUser, logout } from '../services/auth';

const HomeContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const Header = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  color: white;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
  letter-spacing: -0.5px;
`;

const AuthButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
`;

const LoginButton = styled(Button)`
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const SignupButton = styled(Button)`
  background: white;
  color: #1a1a2e;
  
  &:hover {
    background: #f0f0f0;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid white;
`;

const UserName = styled.span`
  color: white;
  font-weight: 500;
`;

function Home() {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    loading: true
  });

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        // If already authenticated, redirect to dashboard
        navigate('/dashboard');
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false
        });
      }
    };

    checkAuth();
  }, [navigate]);

  // Handle Google Sign-In success
  const handleGoogleSignIn = async (credential, decodedUserInfo) => {
    console.log('Google Sign-In credential received in Home component:', credential ? 'yes (length: ' + credential.length + ')' : 'no');
    console.log('Decoded user info:', decodedUserInfo);
    setAuthState(prev => ({ ...prev, loading: true }));
    
    try {
      console.log('Calling googleSignIn with credential');
      // If we have decoded user info from the JWT, use it to enhance the sign-in experience
      const result = await googleSignIn(credential, decodedUserInfo);
      console.log('Sign-in result:', result);
      
      if (result.isAuthenticated) {
        console.log('Authentication successful, redirecting to dashboard');
        // Redirect to dashboard on successful authentication
        navigate('/dashboard');
      } else {
        console.log('Authentication failed:', result.error || 'Unknown reason');
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: result.error
        });
      }
    } catch (error) {
      console.error('Sign-in error:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: error.message || 'Failed to sign in'
      });
    }
  };

  // Handle logout
  const handleLogout = async () => {
    setAuthState(prev => ({ ...prev, loading: true }));
    
    try {
      await logout();
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false
      });
    } catch (error) {
      console.error('Logout error:', error);
      setAuthState(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <HomeContainer>
      <DynamicBackground />
      
      <Header>
        <Logo>What Gov</Logo>
        <AuthButtons>
          {authState.loading ? (
            <div>Loading...</div>
          ) : authState.isAuthenticated ? (
            <UserProfile>
              {authState.user?.pictureUrl && (
                <UserAvatar src={authState.user.pictureUrl} alt={authState.user.name} />
              )}
              <UserName>{authState.user?.name}</UserName>
              <LoginButton onClick={handleLogout}>Logout</LoginButton>
            </UserProfile>
          ) : (
            <>
              <GoogleSignInButton onSuccess={handleGoogleSignIn} />
              <SignupButton>Sign up</SignupButton>
            </>
          )}
        </AuthButtons>
      </Header>
      
      <ParliamentIllustration />
      <ChatWindow />
    </HomeContainer>
  );
}

export default Home;
